import firebaseApp from 'features/login/utils/initFirebase';
import { getPersistor } from 'store/index';
import {
    getAuth,
    signOut,
    getIdToken,
    getIdTokenResult,
    GoogleAuthProvider,
    FacebookAuthProvider,
    OAuthProvider
} from 'firebase/auth';
import { revokeOtpApi } from 'api/otp';

export const auth = getAuth(firebaseApp);
// Default the auth locale to the browser language so verification emails sent
// before login (signup / forgot-password / email verify) go out in the user's
// browser language. After login, switchLocale overrides this with the account
// language. Google Identity Platform translates the email content itself.
auth.useDeviceLanguage();

export const firebaseProviderIds = {
    EMAIL: 'email', // for fever use
    PASSWORD: 'password',
    EMAIL_LINK: 'emailLink',
    FACEBOOK: 'facebook.com',
    GOOGLE: 'google.com',
    LINE: 'oidc.line',
    MICROSOFT: 'microsoft.com'
};

export const firebaseProviderPlatformMapping = {
    [firebaseProviderIds.FACEBOOK]: 'Facebook',
    [firebaseProviderIds.GOOGLE]: 'Google',
    [firebaseProviderIds.LINE]: 'Line',
    [firebaseProviderIds.MICROSOFT]: 'Microsoft'
};

// Client SDK 的錯誤碼，完整清單見 https://firebase.google.com/docs/reference/js/auth#autherrorcodes
// （注意不是 Admin SDK 的 https://firebase.google.com/docs/auth/admin/errors，兩者命名不同）
// 只收錄本專案實際處理的錯誤碼；新增前請先確認該字串存在於 SDK 的 AuthErrorCodes。
export const firebaseApiErrCodes = {
    // --- 登入憑證 ---
    // 開啟 Email Enumeration Protection 後，後端把 EMAIL_NOT_FOUND / INVALID_PASSWORD 合併成
    // INVALID_LOGIN_CREDENTIALS；user-not-found / wrong-password 只在尚未開啟保護的環境才會出現，保留以相容。
    // https://cloud.google.com/identity-platform/docs/admin/email-enumeration-protection
    //
    // INVALID_LOGIN_CREDENTIALS 在 SDK 端的錯誤碼依版本而不同：
    // - firebase 9.x（本專案 9.23.0）的 SERVER_ERROR_MAP 沒收錄它，SDK 會把字串轉小寫、底線換成
    //   連字號，得到 auth/invalid-login-credentials。它不在 AuthErrorCodes 裡，是上方規則的例外。
    // - 新版 SDK 才把它對應到 auth/invalid-credential。兩個都要接，升級 SDK 時才不必再改。
    AUTH_INVALID_LOGIN_CREDENTIALS: 'auth/invalid-login-credentials',
    AUTH_INVALID_CREDENTIAL: 'auth/invalid-credential',
    AUTH_USER_NOT_FOUND: 'auth/user-not-found',
    AUTH_WRONG_PASSWORD: 'auth/wrong-password',

    // --- 密碼政策 ---
    // Require enforcement 模式下 Firebase 拒絕不合規密碼時丟出的錯誤碼。
    AUTH_PASSWORD_DOES_NOT_MEET_REQUIREMENTS: 'auth/password-does-not-meet-requirements', // pragma: allowlist secret
    // 政策關閉時後端預設 6 碼檢查所使用的錯誤碼。enforcement mode 切換的過渡期間兩者都可能出現。
    AUTH_WEAK_PASSWORD: 'auth/weak-password', // pragma: allowlist secret

    // --- 帳號 / 第三方綁定 ---
    AUTH_CREDENTIAL_ALREADY_IN_USE: 'auth/credential-already-in-use',
    // 要第三方登入 (Ex: Line) 的 email 已經被其他第三方登入 (Ex: Google) 綁定過，請直接登入該第三方登入 (Ex: Google) ，再手動綁定其第三方登入 (Ex: Line)
    AUTH_ACCOUNT_EXISTS_WITH_DIFF_CREDENTIAL: 'auth/account-exists-with-different-credential',
    AUTH_EMAIL_ALREADY_IN_USE: 'auth/email-already-in-use',
    // Email 驗證連結信打開後，目標 Email 已經被綁定，所以Email 驗證連結信如果打開第二次會產生此 error
    AUTH_PROVIDER_ALREADY_LINKED: 'auth/provider-already-linked',

    // --- Email 驗證連結 (OOB code) ---
    // 此 browser 登入的 firebase account 與要 email 認證的 firebase account 是不同 account
    AUTH_INVALID_ACTION_CODE: 'auth/invalid-action-code',
    // 網址上 qs 的 OOB_CODE 已經過期
    AUTH_EXPIRED_ACTION_CODE: 'auth/expired-action-code',

    // --- 呼叫端 / 瀏覽器環境 ---
    // Firebase API 參數有誤
    AUTH_ARGUMENT_ERROR: 'auth/argument-error',
    // Safari 或是 chrome 有開啟「阻擋彈出式視窗」導致流程失敗
    AUTH_POPUP_BLOCKED: 'auth/popup-blocked',
    AUTH_POPUP_CLOSED_BY_USER: 'auth/popup-closed-by-user'
};

// 使用 OAuthProvider 的登入方式
const useOAuthProvider = [firebaseProviderIds.LINE, firebaseProviderIds.MICROSOFT];
export const isUseOAuthProvider = providerId => useOAuthProvider.indexOf(providerId) !== -1;

export const Providers = {
    [firebaseProviderIds.GOOGLE]: GoogleAuthProvider,
    [firebaseProviderIds.FACEBOOK]: FacebookAuthProvider,
    [firebaseProviderIds.LINE]: OAuthProvider,
    [firebaseProviderIds.MICROSOFT]: OAuthProvider
};

export const firebaseEmailLinkQueryStrKeys = ['apiKey', 'oobCode', 'mode', 'lang'];

export function getProviderIdFromUser() {
    return auth.currentUser.providerData.map(p => p.providerId);
}

// Get signInProvider from auth
export async function getSignInProvider() {
    if (auth.currentUser) {
        const idTokenResult = await getIdTokenResult(auth.currentUser);
        return idTokenResult.signInProvider;
    }
    return null;
}

export async function signout() {
    const providerId = await getSignInProvider();
    if (providerId === firebaseProviderIds.PASSWORD) {
        await revokeOtpApi();
    }

    const persistor = getPersistor();

    // clear redux-persist data, so that localStorage.getItem(persist:root) will be empty
    if (persistor) {
        await persistor.purge();
    }

    await signOut(auth);
}

export function getFirebaseUserUId() {
    const { uid } = auth.currentUser ?? {};
    return uid;
}

const EXPIRE_IN_MINUTES = 30 * 60 * 1000;
let timeStamp = Date.now();
let firebaseToken = '';

export async function getFirebaseIdToken() {
    if (auth.currentUser) {
        const t = await getIdToken(auth.currentUser);
        timeStamp = Date.now();
        firebaseToken = t;
        return t;
    } else {
        return '';
    }
}

export const firebaseTokenStaticGetter = {
    get token() {
        const now = Date.now();
        if (now - timeStamp >= EXPIRE_IN_MINUTES) {
            getFirebaseIdToken();
        }
        return firebaseToken;
    }
};

export const providersRequiringOtp = [firebaseProviderIds.PASSWORD];
